import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { SITE } from "@/lib/site";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin/leads" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <a href="/" className="flex items-center justify-center mb-8" data-testid="admin-back-home-link">
          <img src={SITE.logo.horizontal} alt={SITE.name} className="h-12 w-auto object-contain" />
        </a>
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-10">
          <div className="w-12 h-12 rounded-2xl bg-[#002570] text-white flex items-center justify-center mb-5">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="font-rubik text-2xl md:text-3xl font-bold text-[#0f172a]">Admin Portal</h1>
          <p className="mt-2 font-inter text-sm text-slate-600">Sign in to manage incoming leads.</p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="admin-email">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@educators.tvm"
                  className="w-full bg-white rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                />
              </div>
            </div>
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="admin-password">Password</label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3.5 font-inter font-medium transition-all"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center font-inter text-xs text-slate-500">
          Authorized personnel only. <a href="/" className="text-[#002570] font-medium hover:underline">Back to website</a>
        </p>
      </div>
    </div>
  );
}
